import {LoadingSkeleton} from '@/components/app/feedback';
import {StatusBadge} from '@/components/app/feedback';
import {cn} from '@/lib/utils';
import {BiotwinBasics} from '../types';
import {calculateBMI} from '../../score/utils/calculateBmi';
import {calculateBMIPercentile} from '../../score/utils/calculateBmiPercentile';
import {calculateWeightForLengthPercentile} from '../../score/utils/calculateWeightForLengthPercentile';
export interface BiotwinBasicsViewerProps {
  className?: string;
  isLoading?: boolean;
  short?: boolean;
  tagClassName?: string;
  value: Partial<BiotwinBasics> | null | undefined;
}
export const BiotwinBasicsViewer = ({className, isLoading, short, tagClassName, value}: BiotwinBasicsViewerProps) => {
  return <div className={className}>{short ? renderShort() : renderLong()}</div>;
  function renderShort() {
    return renderBasicsTag(formatBiotwinBasics(value, 'short'));
  }
  function renderLong() {
    return (
      <>
        {renderBasicsTag(
          formatBiotwinBasics(value, {
            age: 'long',
          }),
        )}
        {renderBasicsTag(
          formatBiotwinBasics(value, {
            gender: 'long',
          }),
        )}
        {renderBasicsTag(
          formatBiotwinBasics(value, {
            height: 'long',
          }),
        )}
        {renderBasicsTag(
          formatBiotwinBasics(value, {
            weight: 'long',
          }),
        )}
        {isBMIVisible() &&
          renderBasicsTag(
            formatBiotwinBasics(value, {
              bmi: 'long',
            }),
          )}
        {isBMIPercentileVisible() &&
          renderBasicsTag(
            formatBiotwinBasics(value, {
              bmiPercentile: 'long',
            }),
            true,
          )}
        {isWFLPercentileVisible() &&
          renderBasicsTag(
            formatBiotwinBasics(value, {
              wflPercentile: 'long',
            }),
            true,
          )}
      </>
    );
  }
  function renderBasicsTag(value: string, requireValue = false) {
    if (isLoading) {
      return (
        <LoadingSkeleton
          className={cn(tagClassName, 'w-fit flex-1')}
          height="1.75rem"
          shape="rectangle"
          width="10rem"
        />
      );
    }
    const hasValue = value !== '-';
    if (requireValue && !hasValue) {
      return null;
    }
    return (
      <StatusBadge
        className={cn(
          'text-sm w-fit flex-1 font-normal bg-zinc-300 min-w-fit!',
          {
            'text-gray-500': !hasValue,
            'text-foreground': hasValue,
          },
          tagClassName,
        )}
      >
        {value}
      </StatusBadge>
    );
  }
  function isBMIVisible() {
    const ageInMonths = getAgeInMonths(value);
    return ageInMonths != null && ageInMonths >= 240;
  }
  function isBMIPercentileVisible() {
    const ageInMonths = getAgeInMonths(value);
    return ageInMonths != null && ageInMonths >= 24 && ageInMonths < 240;
  }
  function isWFLPercentileVisible() {
    const ageInMonths = getAgeInMonths(value);
    return ageInMonths != null && ageInMonths < 24;
  }
};
export const formatBiotwinBasics = (
  biotwinBasics: Partial<BiotwinBasics> | undefined | null,
  options:
    | 'short'
    | 'long'
    | {
        age?: 'short' | 'long';
        bmi?: 'short' | 'long';
        bmiPercentile?: 'short' | 'long';
        height?: 'short' | 'long';
        gender?: 'short' | 'long';
        weight?: 'short' | 'long';
        wflPercentile?: 'short' | 'long';
      },
) => {
  if (biotwinBasics == null) {
    return '';
  }
  const {gender, height, heightUnit, weight, weightUnit} = biotwinBasics;
  const parts: string[] = [];
  if (typeof options === 'string') {
    if (options === 'short') {
      options = {
        age: 'short',
        gender: 'short',
        height: 'short',
        weight: 'short',
      };
    } else {
      options = {
        age: 'long',
        gender: 'long',
        height: 'long',
        weight: 'long',
      };
    }
  }
  if (options.age != null) {
    const ageInMonths = getAgeInMonths(biotwinBasics);
    const ageInYears = getAgeInYears(biotwinBasics);
    if (ageInMonths == null || ageInYears == null) {
      parts.push('-');
    } else if (options.age === 'short') {
      parts.push(ageInMonths <= 60 ? `${ageInMonths}m` : `${ageInYears}y`);
    } else {
      parts.push(
        ageInMonths <= 60
          ? `${ageInMonths} month${ageInMonths > 1 ? 's' : ''}`
          : `${ageInYears} year${ageInYears > 1 ? 's' : ''}`,
      );
    }
  }
  if (options.gender != null) {
    if (gender == null) {
      parts.push('-');
    } else if (options.gender === 'short') {
      parts.push(gender.slice(0, 1).toUpperCase());
    } else {
      parts.push(`${gender.slice(0, 1).toUpperCase()}${gender.slice(1)}`);
    }
  }
  if (options.height != null) {
    if (height == null || heightUnit == null) {
      parts.push('-');
    } else if (options.height === 'short') {
      parts.push(`${height} ${heightUnit}`);
    } else {
      parts.push(`${height} ${heightUnit}`);
    }
  }
  if (options.weight != null) {
    if (weight == null || weightUnit == null) {
      parts.push('-');
    } else if (options.weight === 'short') {
      parts.push(`${weight} ${weightUnit}`);
    } else {
      parts.push(`${weight} ${weightUnit}`);
    }
  }
  if (options.bmi != null) {
    const bmi = calculateBMIFromBiotwinBasics(biotwinBasics);
    if (bmi == null) {
      parts.push('-');
    } else {
      parts.push(formatBMI(bmi));
    }
  }
  if (options.bmiPercentile != null) {
    const bmiPercentile = calculateBMIPercentileFromBiotwinBasics(biotwinBasics);
    if (bmiPercentile == null) {
      parts.push('-');
    } else {
      parts.push(`BMI: ${bmiPercentile}th pct`);
    }
  }
  if (options.wflPercentile != null) {
    const weightForLengthPercentile = calculateWeightForLengthPercentileFromBiotwinBasics(biotwinBasics);
    if (weightForLengthPercentile == null) {
      parts.push('-');
    } else {
      parts.push(`WFL: ${weightForLengthPercentile}th pct`);
    }
  }
  return parts.join(' ');
};
function formatBMI(bmi: number) {
  return `BMI: ${bmi.toFixed(1)}`;
}
function getAgeInMonths(biotwinBasics: Partial<BiotwinBasics> | undefined | null) {
  if (biotwinBasics == null || biotwinBasics.age == null || biotwinBasics.ageUnit == null) {
    return null;
  }
  const {age, ageUnit} = biotwinBasics;
  return ageUnit === 'month' ? age : age * 12;
}
function getAgeInYears(biotwinBasics: Partial<BiotwinBasics> | undefined | null) {
  if (biotwinBasics == null || biotwinBasics.age == null || biotwinBasics.ageUnit == null) {
    return null;
  }
  const {age, ageUnit} = biotwinBasics;
  return ageUnit === 'year' ? age : Math.floor(age / 12);
}
function calculateBMIFromBiotwinBasics(biotwinBasics: Partial<BiotwinBasics> | undefined | null) {
  if (biotwinBasics == null) {
    return null;
  }
  const {height, heightUnit, weight, weightUnit} = biotwinBasics;
  if (height == null || heightUnit == null || weight == null || weightUnit == null) {
    return null;
  }
  try {
    const bmiResult = calculateBMI({
      height,
      heightUnit,
      weight,
      weightUnit,
    });
    return bmiResult.bmi;
  } catch {
    return null;
  }
}
function calculateBMIPercentileFromBiotwinBasics(biotwinBasics: Partial<BiotwinBasics> | undefined | null) {
  if (biotwinBasics == null) {
    return null;
  }
  const {age, ageUnit, gender} = biotwinBasics;
  const ageInYears = age == null || ageUnit == null ? null : ageUnit === 'year' ? age : age / 12;
  const bmi = calculateBMIFromBiotwinBasics(biotwinBasics);
  if (ageInYears == null || gender == null || bmi == null) {
    return null;
  }
  try {
    const bmiPercentileResult = calculateBMIPercentile({
      age: ageInYears,
      bmi,
      sex: gender,
    });
    return bmiPercentileResult.percentile;
  } catch {
    return null;
  }
}
function calculateWeightForLengthPercentileFromBiotwinBasics(biotwinBasics: Partial<BiotwinBasics> | undefined | null) {
  if (biotwinBasics == null) {
    return null;
  }
  const {height, heightUnit, gender, weight, weightUnit} = biotwinBasics;
  if (height == null || heightUnit == null || gender == null || weight == null || weightUnit == null) {
    return null;
  }
  try {
    const weightForLengthPercentileResult = calculateWeightForLengthPercentile({
      length: height,
      lengthUnit: heightUnit,
      sex: gender,
      weight,
      weightUnit,
    });
    return weightForLengthPercentileResult.percentile;
  } catch {
    return null;
  }
}
